<script setup lang="ts">
/**
 * The ☰ menu, in three groups: the screens, in the tab bar's order and with the tide first
 * so there is always a way home; the reader's own settings; and what QuickTide is and how it
 * works. The entries that are sections of Settings open it at the right place -- and
 * "update" presses the button there too, so it says what it found.
 */
import { watch, nextTick, ref } from 'vue'
import { useRoute, useRouter, type RouteLocationRaw } from 'vue-router'
import { useAppStore } from '../store'
import { menuOpen } from '../lib/menu'
import type { MessageKey } from '../i18n'

const store = useAppStore()
const route = useRoute()
const router = useRouter()
const panel = ref<HTMLElement | null>(null)

interface Item { label: MessageKey; to?: RouteLocationRaw; href?: string; icon: string; group: number }
const ITEMS: Item[] = [
  { label: 'tide', to: '/', group: 1, icon: 'M2 14c3-5 5-5 8 0s5 5 8 0M2 19c3-5 5-5 8 0s5 5 8 0M18 8.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5' },
  { label: 'menuLocation', to: '/locaties', group: 1, icon: 'M12 22s7-7 7-12a7 7 0 0 0-14 0c0 5 7 12 7 12zM12 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5' },
  { label: 'currents', to: '/stroom', group: 1, icon: 'M3 7h11l-3-3M3 7l3 3M21 14H10l3-3M21 14l-3 3' },
  { label: 'weatherTitle', to: '/weer', group: 1, icon: 'M7 18a4 4 0 0 1 0-8 5.5 5.5 0 0 1 10.6-1.5A3.75 3.75 0 0 1 18 18z' },
  { label: 'boat', to: { path: '/instellingen', hash: '#schip' }, group: 2, icon: 'M12 3v13M12 4l6 10h-6M3 17h18l-2 3H5z' },
  { label: 'menuAccount', to: { path: '/instellingen', hash: '#activering' }, group: 2, icon: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8M4 21a8 8 0 0 1 16 0' },
  { label: 'settings', to: '/instellingen', group: 2, icon: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z' },
  { label: 'menuUpdate', to: { path: '/instellingen', query: { bijwerken: '1' }, hash: '#opslag' }, group: 2, icon: 'M12 3v12M7 10l5 5 5-5M4 20h16' },
  { label: 'manual', href: '/help/', group: 3, icon: 'M4 5a2 2 0 0 1 2-2h13v16H6a2 2 0 0 0-2 2zM4 21a2 2 0 0 1 2-2h13v2z' },
  { label: 'menuAbout', to: '/over', group: 3, icon: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20M12 16v-5M12 8h.01' }
]

function go(item: Item) {
  menuOpen.value = false
  if (item.to) void router.push(item.to)
}

// a new screen, however it was reached, puts the menu away
watch(() => route.fullPath, () => { menuOpen.value = false })
// open: the first entry has the focus, so a keyboard or a screen reader starts in the menu
watch(menuOpen, async open => {
  if (!open) return
  await nextTick()
  panel.value?.querySelector<HTMLElement>('.menuitem')?.focus()
})
</script>

<template>
  <Transition name="menu">
    <div v-if="menuOpen" class="menuback" @click.self="menuOpen = false" @keydown.esc="menuOpen = false">
      <nav ref="panel" class="sidemenu" role="dialog" aria-modal="true" :aria-label="store.t('menu')">
        <template v-for="(item, i) in ITEMS" :key="item.label">
          <hr v-if="i > 0 && item.group !== ITEMS[i - 1]!.group" class="menurule">
          <a v-if="item.href" class="menuitem" :href="item.href" target="_blank" rel="noopener" @click="menuOpen = false">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path :d="item.icon" /></svg>
            <span>{{ store.t(item.label) }}</span>
          </a>
          <button v-else type="button" class="menuitem" @click="go(item)">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path :d="item.icon" /></svg>
            <span>{{ store.t(item.label) }}</span>
          </button>
        </template>
      </nav>
    </div>
  </Transition>
</template>
